import React, { useEffect, useState, useCallback } from "react";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";

const DEFAULT_US_SIZES = [
  "2T", "3T", "4T", "5T",
  "YXXS", "YXS", "YS", "YM", "YL", "YXL",
  "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL", "6XL"
];

const DEFAULT_INDIAN_SIZES = [
  "1-2 YR", "3-4 YR", "5-6 YR", "7-8 YR", "9-10 YR", "11-12 YR",
  "S", "M", "L", "XL", "2XL", "3XL",
  '52"', '56"', '68"'
];

export default function TShirtsPage() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("apparel", "edit");
  const [campers, setCampers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [savingMap, setSavingMap] = useState({}); // { camperId: "saving" | "saved" | "error" }
  const [error, setError] = useState("");
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(true);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("current");
  const [settingsYear, setSettingsYear] = useState("2027");

  // Inventory & Choice Tabs
  const [sizeSystemTab, setSizeSystemTab] = useState("US"); // "US" | "INDIAN"
  const [stockUs, setStockUs] = useState({});
  const [stockIndian, setStockIndian] = useState({});
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [modalStockTab, setModalStockTab] = useState("US"); // "US" | "INDIAN"
  const [stockFormUs, setStockFormUs] = useState({});
  const [stockFormIndian, setStockFormIndian] = useState({});
  const [savingStock, setSavingStock] = useState(false);

  const [customUsSizes, setCustomUsSizes] = useState([]);
  const [deletedUsSizes, setDeletedUsSizes] = useState([]);

  const [customIndianSizes, setCustomIndianSizes] = useState([]);
  const [deletedIndianSizes, setDeletedIndianSizes] = useState([]);

  const fetchSettings = useCallback(() => {
    api.get("/api/settings/")
      .then(res => {
        const settings = res.data.settings || {};
        if (settings.current_camp_year) {
          setSettingsYear(settings.current_camp_year);
        }
        try {
          if (settings.tshirt_stock_us) {
            setStockUs(JSON.parse(settings.tshirt_stock_us));
          }
          if (settings.tshirt_stock_indian) {
            setStockIndian(JSON.parse(settings.tshirt_stock_indian));
          }
          if (settings.custom_us_sizes) {
            setCustomUsSizes(JSON.parse(settings.custom_us_sizes));
          }
          if (settings.deleted_us_sizes) {
            setDeletedUsSizes(JSON.parse(settings.deleted_us_sizes));
          }
          if (settings.custom_indian_sizes) {
            setCustomIndianSizes(JSON.parse(settings.custom_indian_sizes));
          }
          if (settings.deleted_indian_sizes) {
            setDeletedIndianSizes(JSON.parse(settings.deleted_indian_sizes));
          }
        } catch (e) {
          console.error("Failed to parse stock settings", e);
        }
      })
      .catch(() => {});
  }, []);

  const handleAddCustomUsSize = async (sizeName) => {
    const trimmed = sizeName?.trim();
    if (!trimmed) return;

    const newDeleted = deletedUsSizes.filter(s => s !== trimmed);
    setDeletedUsSizes(newDeleted);

    if (!customUsSizes.includes(trimmed) && !DEFAULT_US_SIZES.includes(trimmed)) {
      const updated = [...customUsSizes, trimmed];
      setCustomUsSizes(updated);
      try {
        await api.post("/api/settings/", {
          custom_us_sizes: JSON.stringify(updated),
          deleted_us_sizes: JSON.stringify(newDeleted)
        });
      } catch {
        setError("Failed to save custom US T-Shirt size.");
      }
    } else if (deletedUsSizes.includes(trimmed)) {
      try {
        await api.post("/api/settings/", {
          deleted_us_sizes: JSON.stringify(newDeleted)
        });
      } catch {
        setError("Failed to restore US T-Shirt size.");
      }
    }
  };

  const handleDeleteUsSize = async (sizeName) => {
    const claimedCount = usSizeCounts[sizeName] || 0;
    if (claimedCount > 0) {
      await alert(`Cannot delete US size "${sizeName}" because it is currently assigned to ${claimedCount} camper(s).`);
      return;
    }

    if (await window.confirm(`Are you sure you want to delete unused US size "${sizeName}"?`)) {
      const updatedDeleted = Array.from(new Set([...deletedUsSizes, sizeName]));
      const updatedCustom = customUsSizes.filter(s => s !== sizeName);

      setDeletedUsSizes(updatedDeleted);
      setCustomUsSizes(updatedCustom);

      setStockFormUs(prev => {
        const next = { ...prev };
        delete next[sizeName];
        return next;
      });

      const nextStockUs = { ...stockUs };
      delete nextStockUs[sizeName];
      setStockUs(nextStockUs);

      try {
        await api.post("/api/settings/", {
          deleted_us_sizes: JSON.stringify(updatedDeleted),
          custom_us_sizes: JSON.stringify(updatedCustom),
          tshirt_stock_us: JSON.stringify(nextStockUs)
        });
      } catch {
        setError("Failed to delete US size.");
      }
    }
  };

  const handleAddCustomSize = async (sizeName) => {
    const trimmed = sizeName?.trim();
    if (!trimmed) return;

    const newDeleted = deletedIndianSizes.filter(s => s !== trimmed);
    setDeletedIndianSizes(newDeleted);

    if (!customIndianSizes.includes(trimmed) && !DEFAULT_INDIAN_SIZES.includes(trimmed)) {
      const updated = [...customIndianSizes, trimmed];
      setCustomIndianSizes(updated);
      try {
        await api.post("/api/settings/", {
          custom_indian_sizes: JSON.stringify(updated),
          deleted_indian_sizes: JSON.stringify(newDeleted)
        });
      } catch {
        setError("Failed to save custom Indian T-Shirt size.");
      }
    } else if (deletedIndianSizes.includes(trimmed)) {
      try {
        await api.post("/api/settings/", {
          deleted_indian_sizes: JSON.stringify(newDeleted)
        });
      } catch {
        setError("Failed to restore Indian T-Shirt size.");
      }
    }
  };

  const handleDeleteIndianSize = async (sizeName) => {
    const claimedCount = indianSizeCounts[sizeName] || 0;
    if (claimedCount > 0) {
      await alert(`Cannot delete size "${sizeName}" because it is currently assigned to ${claimedCount} camper(s).`);
      return;
    }

    if (await window.confirm(`Are you sure you want to delete unused size "${sizeName}"?`)) {
      const updatedDeleted = Array.from(new Set([...deletedIndianSizes, sizeName]));
      const updatedCustom = customIndianSizes.filter(s => s !== sizeName);

      setDeletedIndianSizes(updatedDeleted);
      setCustomIndianSizes(updatedCustom);

      setStockFormIndian(prev => {
        const next = { ...prev };
        delete next[sizeName];
        return next;
      });

      const nextStockIndian = { ...stockIndian };
      delete nextStockIndian[sizeName];
      setStockIndian(nextStockIndian);

      try {
        await api.post("/api/settings/", {
          deleted_indian_sizes: JSON.stringify(updatedDeleted),
          custom_indian_sizes: JSON.stringify(updatedCustom),
          tshirt_stock_indian: JSON.stringify(nextStockIndian)
        });
      } catch {
        setError("Failed to delete size.");
      }
    }
  };

  const activeUsSizes = Array.from(
    new Set([
      ...DEFAULT_US_SIZES.filter(sz => !deletedUsSizes.includes(sz)),
      ...customUsSizes.filter(sz => !deletedUsSizes.includes(sz)),
      ...campers.map(c => c.tshirt_size?.trim()).filter(Boolean)
    ])
  );

  const activeIndianSizes = Array.from(
    new Set([
      ...DEFAULT_INDIAN_SIZES.filter(sz => !deletedIndianSizes.includes(sz)),
      ...customIndianSizes.filter(sz => !deletedIndianSizes.includes(sz)),
      ...campers.map(c => c.indian_size?.trim()).filter(Boolean)
    ])
  );

  const fetchCampers = useCallback(() => {
    setLoading(true);
    // Fetch all campers (non-paginated) to display all families together
    api.get(`/api/campers/?page=1&per_page=-1&year=${selectedYear}`)
      .then(r => {
        setCampers(r.data.campers);
        if (r.data.years) {
          setYears(r.data.years);
        }
      })
      .catch(() => setError("Failed to load campers."))
      .finally(() => setLoading(false));
  }, [selectedYear]);

  useEffect(() => {
    fetchCampers();
    fetchSettings();
  }, [fetchCampers, fetchSettings]);

  const handleOpenStockModal = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setStockFormUs({ ...stockUs });
    setStockFormIndian({ ...stockIndian });
    setIsStockModalOpen(true);
  };

  const handleSaveStock = async (e) => {
    e.preventDefault();
    setSavingStock(true);
    setError("");
    try {
      await api.post("/api/settings/", {
        tshirt_stock_us: JSON.stringify(stockFormUs),
        tshirt_stock_indian: JSON.stringify(stockFormIndian)
      });
      setStockUs(stockFormUs);
      setStockIndian(stockFormIndian);
      setIsStockModalOpen(false);
      fetchSettings();
    } catch (err) {
      console.error("Save stock error:", err);
      setError(err.response?.data?.error || "Failed to save T-Shirt inventory stock limit settings.");
    } finally {
      setSavingStock(false);
    }
  };

  const handleTshirtUpdate = async (camperId, field, value) => {
    const key = `${camperId}-${field}`;
    setSavingMap(prev => ({ ...prev, [key]: "saving" }));

    // Allow selecting both US size and Indian size independently
    const payload = { [field]: value };

    try {
      await api.post(`/api/tshirts/`, { camper_id: camperId, ...payload })
        .catch(async () => {
          // Fallback to camper update endpoint if tshirt endpoint doesn't exist
          await api.put(`/api/campers/${camperId}`, payload);
        });
      
      setSavingMap(prev => ({ ...prev, [key]: "saved" }));
      
      setCampers(prev => prev.map(c => {
        if (c.id === camperId) {
          return { ...c, [field]: value };
        }
        return c;
      }));

      setTimeout(() => {
        setSavingMap(prev => {
          const updated = { ...prev };
          delete updated[key];
          return updated;
        });
      }, 2000);
    } catch {
      setSavingMap(prev => ({ ...prev, [key]: "error" }));
    }
  };

  // Filter campers based on search query
  const filteredCampers = (() => {
    if (!search) return campers;
    const query = search.toLowerCase();
    const matchingFamilyGroups = new Set(
      campers
        .filter(c => 
          c.full_name?.toLowerCase().includes(query) ||
          c.family_group?.toLowerCase().includes(query) ||
          c.guardian_name?.toLowerCase().includes(query)
        )
        .map(c => c.family_group)
        .filter(Boolean)
    );
    return campers.filter(c => {
      const matchesSearch = 
        c.full_name?.toLowerCase().includes(query) ||
        c.family_group?.toLowerCase().includes(query) ||
        c.guardian_name?.toLowerCase().includes(query);
      const belongsToMatchingFamily = c.family_group && matchingFamilyGroups.has(c.family_group);
      return matchesSearch || belongsToMatchingFamily;
    });
  })();

  const handleExportExcel = () => {
    const escapeCell = (val) => `"${String(val ?? "").replace(/"/g, '""')}"`;
    const headers = ["Camper Name", "Family Group", "US Size", "Indian Size"];
    const rows = filteredCampers.map(c => [
      c.full_name || "",
      c.family_group || "",
      c.tshirt_size || "",
      c.indian_size || "",
    ]);
    const csvContent = "\uFEFF" + [headers.join(","), ...rows.map(r => r.map(escapeCell).join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `gca_apparel_orders_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Group campers by family
  const families = {};
  const individuals = [];
  filteredCampers.forEach(c => {
    if (c.family_group) {
      if (!families[c.family_group]) {
        families[c.family_group] = [];
      }
      families[c.family_group].push(c);
    } else {
      individuals.push(c);
    }
  });

  const sortedFamilyKeys = Object.keys(families).sort((a, b) => {
    const numA = parseInt(a, 10);
    const numB = parseInt(b, 10);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.localeCompare(b);
  });

  // Calculate size counts for the summary widget (both US and Indian sizes)
  const usSizeCounts = {};
  const indianSizeCounts = {};
  
  campers.forEach(c => {
    if (c.tshirt_size) {
      usSizeCounts[c.tshirt_size] = (usSizeCounts[c.tshirt_size] || 0) + 1;
    }
    if (c.indian_size) {
      const cleanInd = String(c.indian_size).trim().toUpperCase();
      if (cleanInd) {
        indianSizeCounts[cleanInd] = (indianSizeCounts[cleanInd] || 0) + 1;
      }
    }
  });

  const totalUsCount = Object.values(usSizeCounts).reduce((a, b) => a + b, 0);
  const totalIndianCount = Object.values(indianSizeCounts).reduce((a, b) => a + b, 0);

  const totalUsStock = Object.values(stockUs).reduce((sum, val) => {
    const num = parseInt(val, 10);
    return !isNaN(num) && num > 0 ? sum + num : sum;
  }, 0);

  const totalIndianStock = Object.values(stockIndian).reduce((sum, val) => {
    const num = parseInt(val, 10);
    return !isNaN(num) && num > 0 ? sum + num : sum;
  }, 0);

  const totalUsRemaining = totalUsStock > 0 ? totalUsStock - totalUsCount : null;
  const totalIndianRemaining = totalIndianStock > 0 ? totalIndianStock - totalIndianCount : null;

  return (
    <>
      <div className="top-bar">
        <h1>Apparel</h1>
        <span className="text-muted">{campers.length} campers total</span>
      </div>

      <div className="page-body">
        {error && <div className="alert alert-error">{error}</div>}

        <div className="search-bar" style={{ marginBottom: 20 }}>
          <div className="search-input-wrap" style={{ flex: 1 }}>
            <span className="search-icon">🔍</span>
            <input
              className="form-input"
              placeholder="Search by name, family group, or guardian…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select className="form-select" style={{ width: 180, marginLeft: 10 }} value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
            <option value="current">Active Year ({settingsYear})</option>
            {years.map(y => (
              <option key={y} value={String(y)}>{y}</option>
            ))}
          </select>
          <button
            className="btn btn-outline"
            onClick={handleExportExcel}
            style={{ marginLeft: 10, height: 38, padding: "0 16px", display: "flex", alignItems: "center", gap: 6, fontSize: "0.85rem", fontWeight: 600, whiteSpace: "nowrap" }}
          >
            📥 Export Excel
          </button>
        </div>

        {/* T-Shirt Inventory & Stock Tracking Widget */}
        {!loading && campers.length > 0 && (
          <div className="card" style={{ marginBottom: 28, padding: "16px" }}>
            <div 
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", userSelect: "none" }}
              onClick={() => setIsSummaryExpanded(prev => !prev)}
            >
              <h3 style={{ fontSize: "1rem", color: "var(--forest)", margin: 0, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span>📦 T-Shirt Inventory & Remaining Stock</span>
                <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)", fontWeight: "normal", background: "rgba(34, 76, 56, 0.06)", padding: "3px 10px", borderRadius: 12 }}>
                  Claimed: {totalUsCount} US Size / {totalIndianCount} Indian Size
                </span>
              </h3>
              <span style={{ fontSize: "1.2rem", color: "var(--muted)", fontWeight: "bold" }}>
                {isSummaryExpanded ? "▼" : "▶"}
              </span>
            </div>
            
            {isSummaryExpanded && (
              <div style={{ marginTop: 16, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
                
                {/* Size System Choice Selector Bar */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 20, background: "rgba(0,0,0,0.02)", padding: "10px 14px", borderRadius: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--forest-mid)" }}>View Inventory For:</span>
                    <div style={{ display: "inline-flex", background: "#fff", padding: 3, borderRadius: 8, border: "1px solid var(--border-color)" }}>
                      <button
                        type="button"
                        className={`btn ${sizeSystemTab === "US" ? "btn-primary" : "btn-ghost"}`}
                        style={{ padding: "4px 12px", fontSize: "0.8rem", borderRadius: 6 }}
                        onClick={() => setSizeSystemTab("US")}
                      >
                        🇺🇸 US Sizes
                      </button>
                      <button
                        type="button"
                        className={`btn ${sizeSystemTab === "INDIAN" ? "btn-primary" : "btn-ghost"}`}
                        style={{ padding: "4px 12px", fontSize: "0.8rem", borderRadius: 6 }}
                        onClick={() => setSizeSystemTab("INDIAN")}
                      >
                        🇮🇳 Indian Sizes
                      </button>
                    </div>
                  </div>

                  {canEdit && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={(e) => handleOpenStockModal(e)}
                      style={{ fontSize: "0.8rem", height: 34, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}
                    >
                      ⚙️ Manage Inventory Stock Limits
                    </button>
                  )}
                </div>

                {/* Inventory Summary KPI Row */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 20 }}>
                  
                  {sizeSystemTab === "US" && (
                    <>
                      <div style={{ background: "rgba(34, 76, 56, 0.05)", border: "1px solid var(--border-color)", padding: "12px 16px", borderRadius: 8 }}>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase" }}>Total Claimed US Shirts</div>
                        <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "var(--forest)", marginTop: 2 }}>
                          {totalUsCount} <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--charcoal)" }}>claimed</span>
                        </div>
                      </div>

                      <div style={{ background: "rgba(34, 76, 56, 0.05)", border: "1px solid var(--border-color)", padding: "12px 16px", borderRadius: 8 }}>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase" }}>Configured US Stock Limit</div>
                        <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "var(--forest-mid)", marginTop: 2 }}>
                          {totalUsStock > 0 ? `${totalUsStock} shirts` : "No Limit Set"}
                        </div>
                      </div>

                      <div style={{ background: totalUsRemaining !== null && totalUsRemaining < 0 ? "rgba(220, 38, 38, 0.06)" : "rgba(34, 76, 56, 0.08)", border: "1px solid var(--border-color)", padding: "12px 16px", borderRadius: 8 }}>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase" }}>Remaining US Stock</div>
                        <div style={{ fontSize: "1.3rem", fontWeight: 800, color: totalUsRemaining !== null && totalUsRemaining >= 0 ? "var(--forest)" : totalUsRemaining !== null ? "var(--red)" : "var(--charcoal)", marginTop: 2 }}>
                          {totalUsStock > 0 ? (
                            totalUsRemaining >= 0 ? `✅ ${totalUsRemaining} left` : `🔴 ${Math.abs(totalUsRemaining)} over limit`
                          ) : (
                            <span style={{ fontSize: "0.9rem", color: "var(--muted)" }}>No stock limit set</span>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {sizeSystemTab === "INDIAN" && (
                    <>
                      <div style={{ background: "rgba(34, 76, 56, 0.05)", border: "1px solid var(--border-color)", padding: "12px 16px", borderRadius: 8 }}>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase" }}>Total Claimed Indian Shirts</div>
                        <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "var(--forest-mid)", marginTop: 2 }}>
                          {totalIndianCount} <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--charcoal)" }}>claimed</span>
                        </div>
                      </div>

                      <div style={{ background: "rgba(34, 76, 56, 0.05)", border: "1px solid var(--border-color)", padding: "12px 16px", borderRadius: 8 }}>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase" }}>Configured Indian Stock Limit</div>
                        <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "var(--forest-mid)", marginTop: 2 }}>
                          {totalIndianStock > 0 ? `${totalIndianStock} shirts` : "No Limit Set"}
                        </div>
                      </div>

                      <div style={{ background: totalIndianRemaining !== null && totalIndianRemaining < 0 ? "rgba(220, 38, 38, 0.06)" : "rgba(34, 76, 56, 0.08)", border: "1px solid var(--border-color)", padding: "12px 16px", borderRadius: 8 }}>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase" }}>Remaining Indian Stock</div>
                        <div style={{ fontSize: "1.3rem", fontWeight: 800, color: totalIndianRemaining !== null && totalIndianRemaining >= 0 ? "var(--forest)" : totalIndianRemaining !== null ? "var(--red)" : "var(--charcoal)", marginTop: 2 }}>
                          {totalIndianStock > 0 ? (
                            totalIndianRemaining >= 0 ? `✅ ${totalIndianRemaining} left` : `🔴 ${Math.abs(totalIndianRemaining)} over limit`
                          ) : (
                            <span style={{ fontSize: "0.9rem", color: "var(--muted)" }}>No stock limit set</span>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {sizeSystemTab === "BOTH" && (
                    <>
                      {/* US Size KPI Card */}
                      <div style={{ background: totalUsRemaining !== null && totalUsRemaining < 0 ? "rgba(220, 38, 38, 0.06)" : "rgba(34, 76, 56, 0.05)", border: "1px solid var(--border-color)", padding: "12px 16px", borderRadius: 8 }}>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase" }}>🇺🇸 US Size Stock</div>
                        <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "var(--forest)", marginTop: 2 }}>
                          {totalUsCount} <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--charcoal)" }}>claimed</span>
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: 4 }}>
                          {totalUsStock > 0 ? (
                            <>
                              Limit: <strong>{totalUsStock}</strong> &bull;{" "}
                              <span style={{ fontWeight: 700, color: totalUsRemaining >= 0 ? "var(--forest)" : "var(--red)" }}>
                                {totalUsRemaining >= 0 ? `✅ ${totalUsRemaining} left` : `🔴 ${Math.abs(totalUsRemaining)} over limit`}
                              </span>
                            </>
                          ) : (
                            <span style={{ color: "var(--muted)" }}>No stock limit set</span>
                          )}
                        </div>
                      </div>

                      {/* Indian Size KPI Card */}
                      <div style={{ background: totalIndianRemaining !== null && totalIndianRemaining < 0 ? "rgba(220, 38, 38, 0.06)" : "rgba(34, 76, 56, 0.05)", border: "1px solid var(--border-color)", padding: "12px 16px", borderRadius: 8 }}>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase" }}>🇮🇳 Indian Size Stock</div>
                        <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "var(--forest-mid)", marginTop: 2 }}>
                          {totalIndianCount} <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--charcoal)" }}>claimed</span>
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: 4 }}>
                          {totalIndianStock > 0 ? (
                            <>
                              Limit: <strong>{totalIndianStock}</strong> &bull;{" "}
                              <span style={{ fontWeight: 700, color: totalIndianRemaining >= 0 ? "var(--forest)" : "var(--red)" }}>
                                {totalIndianRemaining >= 0 ? `✅ ${totalIndianRemaining} left` : `🔴 ${Math.abs(totalIndianRemaining)} over limit`}
                              </span>
                            </>
                          ) : (
                            <span style={{ color: "var(--muted)" }}>No stock limit set</span>
                          )}
                        </div>
                      </div>

                      {/* Total Camper Allocation Card */}
                      <div style={{ background: "rgba(34, 76, 56, 0.05)", border: "1px solid var(--border-color)", padding: "12px 16px", borderRadius: 8 }}>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase" }}>Campers With T-Shirts</div>
                        {(() => {
                          const uniqueCampersWithShirt = campers.filter(c => Boolean(c.tshirt_size?.trim() || c.indian_size?.trim())).length;
                          return (
                            <>
                              <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "var(--forest)", marginTop: 2 }}>
                                {uniqueCampersWithShirt} <span style={{ fontSize: "0.85rem", fontWeight: 500, color: "var(--text-secondary)" }}>/ {campers.length} campers</span>
                              </div>
                              <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: 4 }}>
                                {totalUsCount} US sizes + {totalIndianCount} Indian sizes
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </>
                  )}

                </div>

                <div style={{ display: "grid", gridTemplateColumns: (sizeSystemTab === "BOTH") ? "1fr auto 1fr" : "1fr", gap: 24 }}>
                  
                  {/* US Sizes Column */}
                  {(sizeSystemTab === "US" || sizeSystemTab === "BOTH") && (
                    <div>
                      <h4 style={{ fontSize: "0.9rem", color: "var(--forest)", marginBottom: 12, borderBottom: "1px solid rgba(0,0,0,0.06)", paddingBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>🇺🇸 US Size Inventory</span>
                        <span style={{ fontSize: "0.75rem", color: "var(--text-secondary)", fontWeight: "normal" }}>{totalUsCount} total claimed</span>
                      </h4>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 10 }}>
                        {activeUsSizes.map(sz => {
                          const claimed = usSizeCounts[sz] || 0;
                          const total = stockUs[sz] !== undefined && stockUs[sz] !== "" ? parseInt(stockUs[sz], 10) : null;
                          const hasStockLimit = total !== null && !isNaN(total);
                          const remaining = hasStockLimit ? total - claimed : null;

                          let badgeClass = "badge-success";
                          let badgeText = `${claimed} claimed`;
                          if (hasStockLimit) {
                            if (remaining > 5) {
                              badgeClass = "badge-success";
                              badgeText = `${remaining} left`;
                            } else if (remaining > 0) {
                              badgeClass = "badge-warning";
                              badgeText = `⚠️ ${remaining} left`;
                            } else {
                              badgeClass = "badge-danger";
                              badgeText = remaining === 0 ? "🔴 Out of Stock" : `🔴 ${Math.abs(remaining)} over limit`;
                            }
                          }

                          return (
                            <div key={sz} style={{ 
                              background: claimed > 0 ? "rgba(34, 76, 56, 0.04)" : "#fafafa", 
                              border: "1px solid var(--border-color)", 
                              borderRadius: 8, 
                              padding: "8px 10px", 
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                              gap: 4
                            }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "var(--forest)" }}>{sz}</span>
                                <span className={`badge ${badgeClass}`} style={{ fontSize: "0.65rem", padding: "1px 5px" }}>
                                  {badgeText}
                                </span>
                              </div>
                              <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: 2 }}>
                                Claimed: <strong style={{ color: "var(--charcoal)" }}>{claimed}</strong>
                                {hasStockLimit && <span> / Total: {total}</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Vertical Divider Line */}
                  {sizeSystemTab === "BOTH" && (
                    <div style={{ width: "2px", background: "rgba(34, 76, 56, 0.25)", borderRadius: "2px", alignSelf: "stretch" }} />
                  )}

                  {/* Indian Sizes Column */}
                  {(sizeSystemTab === "INDIAN" || sizeSystemTab === "BOTH") && (
                    <div>
                      <h4 style={{ fontSize: "0.9rem", color: "var(--forest)", marginBottom: 12, borderBottom: "1px solid rgba(0,0,0,0.06)", paddingBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>🇮🇳 Indian Size Inventory</span>
                        <span style={{ fontSize: "0.75rem", color: "var(--text-secondary)", fontWeight: "normal" }}>{totalIndianCount} total claimed</span>
                      </h4>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 10 }}>
                        {activeIndianSizes.map(sz => {
                          const claimed = indianSizeCounts[sz] || 0;
                          const total = stockIndian[sz] !== undefined && stockIndian[sz] !== "" ? parseInt(stockIndian[sz], 10) : null;
                          const hasStockLimit = total !== null && !isNaN(total);
                          const remaining = hasStockLimit ? total - claimed : null;

                          let badgeClass = "badge-success";
                          let badgeText = `${claimed} claimed`;
                          if (hasStockLimit) {
                            if (remaining > 5) {
                              badgeClass = "badge-success";
                              badgeText = `${remaining} left`;
                            } else if (remaining > 0) {
                              badgeClass = "badge-warning";
                              badgeText = `⚠️ ${remaining} left`;
                            } else {
                              badgeClass = "badge-danger";
                              badgeText = remaining === 0 ? "🔴 Out of Stock" : `🔴 ${Math.abs(remaining)} over limit`;
                            }
                          }

                          return (
                            <div key={sz} style={{ 
                              background: claimed > 0 ? "rgba(34, 76, 56, 0.04)" : "#fafafa", 
                              border: "1px solid var(--border-color)", 
                              borderRadius: 8, 
                              padding: "8px 10px", 
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                              gap: 4
                            }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "var(--forest)" }}>{sz}</span>
                                <span className={`badge ${badgeClass}`} style={{ fontSize: "0.65rem", padding: "1px 5px" }}>
                                  {badgeText}
                                </span>
                              </div>
                              <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: 2 }}>
                                Claimed: <strong style={{ color: "var(--charcoal)" }}>{claimed}</strong>
                                {hasStockLimit && <span> / Total: {total}</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                </div>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-center" style={{ padding: 48, color: "var(--muted)" }}>Loading campers…</div>
        ) : filteredCampers.length === 0 ? (
          <div className="text-center" style={{ padding: 48, color: "var(--muted)" }}>No matching campers found.</div>
        ) : (
          <div className="family-grid">
            {/* Render Families */}
            {sortedFamilyKeys.map(fg => {
              const members = families[fg];
              return (
                <div key={fg} className="family-card">
                  <div className="family-card-header">
                    <h3 className="family-card-title">👨‍👩‍👧‍👦 Family #{fg}</h3>
                    <span className="text-muted" style={{ fontSize: "0.75rem" }}>{members.length} members</span>
                  </div>
                  <div className="family-card-body">
                    {members.map(c => {
                      return (
                        <div key={c.id} className="family-member-row" style={{ padding: "10px 0", borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                          <div className="apparel-member-row">
                            <div className="apparel-member-info">
                              <div className="family-member-name" style={{ fontWeight: 600 }}>{c.full_name}</div>
                            </div>
                            <div className="apparel-member-controls">
                              {/* US Size Select */}
                              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <label style={{ fontSize: "0.65rem", color: "var(--text-secondary)", fontWeight: 600 }}>US Size</label>
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                  <select 
                                    className="form-select" 
                                    style={{ width: 120, padding: "4px 8px", fontSize: "0.8rem", height: "30px" }}
                                    value={c.tshirt_size || ""}
                                    onChange={async (e) => {
                                      const val = e.target.value;
                                      if (val === "__ADD_CUSTOM__") {
                                        const custom = window.prompt("Enter new custom US T-Shirt size:");
                                        if (custom && custom.trim()) {
                                          const clean = custom.trim();
                                          await handleAddCustomUsSize(clean);
                                          handleTshirtUpdate(c.id, "tshirt_size", clean);
                                        }
                                      } else {
                                        handleTshirtUpdate(c.id, "tshirt_size", val);
                                      }
                                    }}
                                    disabled={!canEdit}
                                  >
                                    <option value="">— Select —</option>
                                    {activeUsSizes.map(sz => (
                                      <option key={sz} value={sz}>{sz}</option>
                                    ))}
                                    <option value="__ADD_CUSTOM__">➕ Custom Size...</option>
                                  </select>
                                  {savingMap[`${c.id}-tshirt_size`] === "saving" && <span className="spinner" style={{ width: 12, height: 12, borderWidth: 1.5 }} />}
                                  {savingMap[`${c.id}-tshirt_size`] === "saved" && <span style={{ color: "var(--forest)", fontSize: "0.8rem", fontWeight: 700 }}>✓</span>}
                                  {savingMap[`${c.id}-tshirt_size`] === "error" && <span style={{ color: "var(--red)", fontSize: "0.8rem", fontWeight: 700 }}>⚠️</span>}
                                </div>
                              </div>

                              {/* Indian Size Select */}
                              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <label style={{ fontSize: "0.65rem", color: "var(--text-secondary)", fontWeight: 600 }}>Indian Size</label>
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                  <select 
                                    className="form-select" 
                                    style={{ width: 120, padding: "4px 8px", fontSize: "0.8rem", height: "30px" }}
                                    value={c.indian_size || ""}
                                    onChange={async (e) => {
                                      const val = e.target.value;
                                      if (val === "__ADD_CUSTOM__") {
                                        const custom = window.prompt("Enter new custom Indian T-Shirt size:");
                                        if (custom && custom.trim()) {
                                          const clean = custom.trim();
                                          await handleAddCustomSize(clean);
                                          handleTshirtUpdate(c.id, "indian_size", clean);
                                        }
                                      } else {
                                        handleTshirtUpdate(c.id, "indian_size", val);
                                      }
                                    }}
                                    disabled={!canEdit}
                                  >
                                    <option value="">— Select —</option>
                                    {activeIndianSizes.map(sz => (
                                      <option key={sz} value={sz}>{sz}</option>
                                    ))}
                                    <option value="__ADD_CUSTOM__">➕ Custom Size...</option>
                                  </select>
                                  {savingMap[`${c.id}-indian_size`] === "saving" && <span className="spinner" style={{ width: 12, height: 12, borderWidth: 1.5 }} />}
                                  {savingMap[`${c.id}-indian_size`] === "saved" && <span style={{ color: "var(--forest)", fontSize: "0.8rem", fontWeight: 700 }}>✓</span>}
                                  {savingMap[`${c.id}-indian_size`] === "error" && <span style={{ color: "var(--red)", fontSize: "0.8rem", fontWeight: 700 }}>⚠️</span>}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Render Individuals */}
            {individuals.length > 0 && (
              <div className="family-card" style={{ borderTop: "4px solid var(--forest)" }}>
                <div className="family-card-header" style={{ background: "rgba(34, 76, 56, 0.05)" }}>
                  <h3 className="family-card-title" style={{ color: "var(--forest)" }}>👤 Individual Registrations</h3>
                  <span className="text-muted" style={{ fontSize: "0.75rem" }}>{individuals.length} members</span>
                </div>
                <div className="family-card-body">
                  {individuals.map(c => {
                    return (
                      <div key={c.id} className="family-member-row" style={{ padding: "10px 0", borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <div className="family-member-name" style={{ fontWeight: 600 }}>{c.full_name}</div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            {/* US Size Select */}
                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                              <label style={{ fontSize: "0.65rem", color: "var(--text-secondary)", fontWeight: 600 }}>US Size</label>
                              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <select 
                                   className="form-select" 
                                   style={{ width: 120, padding: "4px 8px", fontSize: "0.8rem", height: "30px" }}
                                   value={c.tshirt_size || ""}
                                   onChange={async (e) => {
                                     const val = e.target.value;
                                     if (val === "__ADD_CUSTOM__") {
                                       const custom = window.prompt("Enter new custom US T-Shirt size:");
                                       if (custom && custom.trim()) {
                                         const clean = custom.trim();
                                         await handleAddCustomUsSize(clean);
                                         handleTshirtUpdate(c.id, "tshirt_size", clean);
                                       }
                                     } else {
                                       handleTshirtUpdate(c.id, "tshirt_size", val);
                                     }
                                   }}
                                   disabled={!canEdit}
                                 >
                                   <option value="">— Select —</option>
                                   {activeUsSizes.map(sz => (
                                     <option key={sz} value={sz}>{sz}</option>
                                   ))}
                                   <option value="__ADD_CUSTOM__">➕ Custom Size...</option>
                                 </select>
                                {savingMap[`${c.id}-tshirt_size`] === "saving" && <span className="spinner" style={{ width: 12, height: 12, borderWidth: 1.5 }} />}
                                  {savingMap[`${c.id}-tshirt_size`] === "saved" && <span style={{ color: "var(--forest)", fontSize: "0.8rem", fontWeight: 700 }}>✓</span>}
                                  {savingMap[`${c.id}-tshirt_size`] === "error" && <span style={{ color: "var(--red)", fontSize: "0.8rem", fontWeight: 700 }}>⚠️</span>}
                              </div>
                            </div>

                            {/* Indian Size Select */}
                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                              <label style={{ fontSize: "0.65rem", color: "var(--text-secondary)", fontWeight: 600 }}>Indian Size</label>
                              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <select 
                                  className="form-select" 
                                  style={{ width: 120, padding: "4px 8px", fontSize: "0.8rem", height: "30px" }}
                                  value={c.indian_size || ""}
                                  onChange={async (e) => {
                                    const val = e.target.value;
                                    if (val === "__ADD_CUSTOM__") {
                                      const custom = window.prompt("Enter new custom Indian T-Shirt size:");
                                      if (custom && custom.trim()) {
                                        const clean = custom.trim();
                                        await handleAddCustomSize(clean);
                                        handleTshirtUpdate(c.id, "indian_size", clean);
                                      }
                                    } else {
                                      handleTshirtUpdate(c.id, "indian_size", val);
                                    }
                                  }}
                                  disabled={!canEdit}
                                >
                                  <option value="">— Select —</option>
                                  {activeIndianSizes.map(sz => (
                                    <option key={sz} value={sz}>{sz}</option>
                                  ))}
                                  <option value="__ADD_CUSTOM__">➕ Custom Size...</option>
                                </select>
                                {savingMap[`${c.id}-indian_size`] === "saving" && <span className="spinner" style={{ width: 12, height: 12, borderWidth: 1.5 }} />}
                                  {savingMap[`${c.id}-indian_size`] === "saved" && <span style={{ color: "var(--forest)", fontSize: "0.8rem", fontWeight: 700 }}>✓</span>}
                                  {savingMap[`${c.id}-indian_size`] === "error" && <span style={{ color: "var(--red)", fontSize: "0.8rem", fontWeight: 700 }}>⚠️</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Manage T-Shirt Inventory Stock Modal */}
      {isStockModalOpen && (
        <div 
          className="modal-backdrop"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
            padding: 16
          }}
          onClick={() => setIsStockModalOpen(false)}
        >
          <div 
            className="modal-content" 
            style={{ 
              background: "#ffffff",
              borderRadius: 12,
              padding: 24,
              width: "100%",
              maxWidth: 680, 
              maxHeight: "90vh", 
              overflowY: "auto",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.25), 0 10px 10px -5px rgba(0, 0, 0, 0.1)" 
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, borderBottom: "1px solid var(--border-color)", paddingBottom: 12 }}>
              <h3 style={{ margin: 0, color: "var(--forest)" }}>⚙️ Manage T-Shirt Inventory Stock</h3>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setIsStockModalOpen(false)}>✕</button>
            </div>
            
            <form onSubmit={handleSaveStock}>
              <p style={{ fontSize: "0.8rem", color: "var(--text-secondary)", marginBottom: 16 }}>
                Set total ordered stock quantity for each T-shirt size. The system automatically calculates remaining stock in real-time.
              </p>

              {(() => {
                const formUsTotal = Object.values(stockFormUs).reduce((sum, val) => {
                  const num = parseInt(val, 10);
                  return !isNaN(num) && num > 0 ? sum + num : sum;
                }, 0);
                const formIndianTotal = Object.values(stockFormIndian).reduce((sum, val) => {
                  const num = parseInt(val, 10);
                  return !isNaN(num) && num > 0 ? sum + num : sum;
                }, 0);

                return (
                  <>
                    {/* Tab Navigation for Modal */}
                    <div style={{ display: "flex", gap: 10, marginBottom: 20, borderBottom: "1px solid var(--border-color)", paddingBottom: 12 }}>
                      <button
                        type="button"
                        className={`btn ${modalStockTab === "US" ? "btn-primary" : "btn-ghost"}`}
                        style={{ fontSize: "0.85rem", padding: "6px 16px", borderRadius: 8, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}
                        onClick={() => setModalStockTab("US")}
                      >
                        🇺🇸 US Size Stock ({formUsTotal} Total)
                      </button>
                      <button
                        type="button"
                        className={`btn ${modalStockTab === "INDIAN" ? "btn-primary" : "btn-ghost"}`}
                        style={{ fontSize: "0.85rem", padding: "6px 16px", borderRadius: 8, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}
                        onClick={() => setModalStockTab("INDIAN")}
                      >
                        🇮🇳 Indian Size Stock ({formIndianTotal} Total)
                      </button>
                    </div>

                    {/* Tab 1: US Sizes Stock Form */}
                    {modalStockTab === "US" && (
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, borderBottom: "1px solid var(--border-color)", paddingBottom: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <h4 style={{ fontSize: "0.9rem", color: "var(--forest-mid)", margin: 0 }}>
                              🇺🇸 US Size Total Stock Limits
                            </h4>
                            <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "var(--forest)" }}>
                              Total US Stock: {formUsTotal}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            style={{ fontSize: "0.75rem", padding: "3px 10px", cursor: "pointer" }}
                            onClick={async () => {
                              const custom = window.prompt("Enter new custom US T-Shirt size (e.g. 7XL, Youth XS):");
                              if (custom && custom.trim()) {
                                await handleAddCustomUsSize(custom.trim());
                              }
                            }}
                          >
                            ➕ Add Custom Size
                          </button>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12 }}>
                          {activeUsSizes.map(sz => {
                            const isUsed = (usSizeCounts[sz] || 0) > 0;
                            return (
                              <div key={sz} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4, background: "rgba(0,0,0,0.02)", padding: "6px 8px", borderRadius: 6, border: "1px solid var(--border-color)" }}>
                                <label style={{ fontSize: "0.8rem", fontWeight: 700, width: 55, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={sz}>{sz}</label>
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                  <input
                                    type="number"
                                    min="0"
                                    className="form-input"
                                    style={{ height: 28, fontSize: "0.8rem", padding: "2px 4px", width: 55 }}
                                    value={stockFormUs[sz] !== undefined ? stockFormUs[sz] : ""}
                                    placeholder="No limit"
                                    onChange={e => setStockFormUs(prev => ({ ...prev, [sz]: e.target.value }))}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    style={{
                                      padding: "1px 4px",
                                      fontSize: "0.75rem",
                                      color: isUsed ? "#a0aec0" : "#e53e3e",
                                      cursor: isUsed ? "not-allowed" : "pointer"
                                    }}
                                    title={isUsed ? `Size currently in use by ${usSizeCounts[sz]} camper(s)` : "Delete unused size"}
                                    onClick={() => handleDeleteUsSize(sz)}
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Tab 2: Indian Sizes Stock Form */}
                    {modalStockTab === "INDIAN" && (
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, borderBottom: "1px solid var(--border-color)", paddingBottom: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <h4 style={{ fontSize: "0.9rem", color: "var(--forest-mid)", margin: 0 }}>
                              🇮🇳 Indian Size Total Stock Limits
                            </h4>
                            <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "var(--forest)" }}>
                              Total Indian Stock: {formIndianTotal}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            style={{ fontSize: "0.75rem", padding: "3px 10px", cursor: "pointer" }}
                            onClick={async () => {
                              const custom = window.prompt("Enter new custom Indian T-Shirt size (e.g. 48\", Special):");
                              if (custom && custom.trim()) {
                                await handleAddCustomSize(custom.trim());
                              }
                            }}
                          >
                            ➕ Add Custom Size
                          </button>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12 }}>
                          {activeIndianSizes.map(sz => {
                            const isUsed = (indianSizeCounts[sz] || 0) > 0;
                            return (
                              <div key={sz} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4, background: "rgba(0,0,0,0.02)", padding: "6px 8px", borderRadius: 6, border: "1px solid var(--border-color)" }}>
                                <label style={{ fontSize: "0.8rem", fontWeight: 700, width: 55, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={sz}>{sz}</label>
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                  <input
                                    type="number"
                                    min="0"
                                    className="form-input"
                                    style={{ height: 28, fontSize: "0.8rem", padding: "2px 4px", width: 55 }}
                                    value={stockFormIndian[sz] !== undefined ? stockFormIndian[sz] : ""}
                                    placeholder="No limit"
                                    onChange={e => setStockFormIndian(prev => ({ ...prev, [sz]: e.target.value }))}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    style={{
                                      padding: "1px 4px",
                                      fontSize: "0.75rem",
                                      color: isUsed ? "#a0aec0" : "#e53e3e",
                                      cursor: isUsed ? "not-allowed" : "pointer"
                                    }}
                                    title={isUsed ? `Size currently in use by ${indianSizeCounts[sz]} camper(s)` : "Delete unused size"}
                                    onClick={() => handleDeleteIndianSize(sz)}
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border-color)", paddingTop: 16 }}>
                      <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--forest)" }}>
                        Configured Limits: {formUsTotal} US stock / {formIndianTotal} Indian stock
                      </div>
                      <div style={{ display: "flex", gap: 10 }}>
                        <button type="button" className="btn btn-secondary" onClick={() => setIsStockModalOpen(false)}>
                          Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={savingStock}>
                          {savingStock ? "Saving Limits..." : "Save Inventory Limits"}
                        </button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </form>
          </div>
        </div>
      )}
    </>
  );
}
